import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { TILE_SIZES, type TileSize } from '../constants.ts';
import styles from '../styles/styles.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { type ProductInternalTools_item$key } from './__generated__/ProductInternalTools_item.graphql.ts';
import { type ProductInternalTools_user$key } from './__generated__/ProductInternalTools_user.graphql.ts';
import { type ProductInternalTools_itemSearch$key } from './__generated__/ProductInternalTools_itemSearch.graphql.ts';

const itemFragment = graphql`
    fragment ProductInternalTools_item on Item {
        serviceId
    }
`;

const userFragment = graphql`
    fragment ProductInternalTools_user on User
    @argumentDefinitions(showMerchandizingTool: { type: "Boolean", defaultValue: false }) {
        hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
            @include(if: $showMerchandizingTool)
        canShowStorefrontOnlyBadge: hasPermission(name: "VIEW_STOREFRONT_ONLY_BADGE")
    }
`;

const itemSearchFragment = graphql`
    fragment ProductInternalTools_itemSearch on ItemSearchQueryConnection {
        pageType
    }
`;

type Props = {
    item: ProductInternalTools_item$key;
    user: ProductInternalTools_user$key | null | undefined;
    itemSearch: ProductInternalTools_itemSearch$key | null | undefined;
    size: TileSize;
};

export const ProductInternalTools: FC<Props> = ({
    item: itemRef,
    user: userRef,
    itemSearch: itemSearchRef,
    size,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    if (size === TILE_SIZES.small) {
        return null;
    }

    const { serviceId: itemId } = item;
    const { hasMerchandizingPermission, canShowStorefrontOnlyBadge } = user || {};
    const { pageType } = itemSearch || {};

    const showMerchandizingTool = hasMerchandizingPermission && itemId;
    const showStorefrontOnlyBadge = canShowStorefrontOnlyBadge && pageType === 'seller';
    if (!showMerchandizingTool && !showStorefrontOnlyBadge) {
        return null;
    }
    return (
        <div className={classnames(dibsCss.absolute, dibsCss.topXxsmall, dibsCss.leftXxsmall)}>
            {showMerchandizingTool && (
                <div className={classnames(dibsCss.hLarge, dibsCss.mbXxsmall)}>
                    <div data-dibs-merchandizing-tool-item={itemId} />
                </div>
            )}
            {showStorefrontOnlyBadge && (
                <div
                    className={classnames(
                        styles.storefrontOnlyPill,
                        dibsCss.pxXsmall,
                        dibsCss.pyXxsmall,
                        dibsCss.textNoir,
                        dibsCss.textBodySmallHeavy,
                        dibsCss.roundedFull
                    )}
                    data-tn="storefront-only"
                >
                    <FormattedMessage
                        id="buyerProductTile.storefrontOnly"
                        defaultMessage="Storefront Only"
                    />
                </div>
            )}
        </div>
    );
};
